import { Redirect, Route, RouteComponentProps } from 'react-router-dom'
import {
  IonApp,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  isPlatform,
  setupIonicReact,
} from '@ionic/react'
import { IonReactRouter } from '@ionic/react-router'

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css'

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css'
import '@ionic/react/css/structure.css'
import '@ionic/react/css/typography.css'

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css'
import '@ionic/react/css/float-elements.css'
import '@ionic/react/css/text-alignment.css'
import '@ionic/react/css/text-transformation.css'
import '@ionic/react/css/flex-utils.css'
import '@ionic/react/css/display.css'

/* Theme variables */
import './theme/variables.css'

import { SignIn, SignUp, SignInVerify } from './pages/auth'
import { DetailNotice, DetailNotification, DetailSell, Main as Notice } from './pages/notification'
import {
  Account,
  AccountDeposit,
  AccountWithdrawal,
  Asset,
  ProductDetail,
  ProductDisposal,
  Faq,
  Main as My,
  Qna,
  QnaDetail,
  QnaRegister,
  Setting,
  SettingAccount,
  SettingAddress,
  SettingWallet,
  SettingProfile,
  SettingNickname,
  SettingCashReceipt,
  ProductDescription,
  ProductCommunity,
  NoticeMy,
  NoticeDetailMy,
} from './pages/myPage'
import { DetailArtMuseum, DetailEvent, Main as Artfair } from './pages/artfair'
import { gridOutline, homeOutline, personOutline, repeatOutline } from 'ionicons/icons'
import { Main as Market, Detail, Trade, ProductCommunityMarket } from './pages/market/template'
import styled from 'styled-components'
import { useContext, useEffect } from 'react'
import { StoresContext } from './provider/StoreProvider'
import { observer } from 'mobx-react'
import { StaticContext } from 'react-router'
import './theme/cardo-alert-one-btn.css'
import { Main as Home, SellHistoryDetail } from './pages/home'
import { IsSignIn, RedirectComponent, RoutePending } from './common'

// test sample
import { Sample as Sample } from './pages/sample'

type routeInfo = { path: string; component: React.FC<RouteComponentProps<any, StaticContext, unknown>> }

setupIonicReact({ mode: 'md' })

const App: React.FC = observer(() => {
  const { authStore, commonStore, notificationStore } = useContext(StoresContext)
  const fallbackUrl = '/isSignIn'

  useEffect(() => {
    authStore.getUserInfo()
    notificationStore.isReadNotification()
    commonStore.categoryList.load()
  }, [])

  const getPublicRoutes = (routes: routeInfo[]) => {
    return routes.map((r, i) => {
      const Component = r.component
      return <Route key={`public${i}`} exact={true} path={r.path} component={Component} />
    })
  }

  const getPrivateRoutes = (routes: routeInfo[]) => {
    return routes.map((r, i) => {
      return (
        <Route
          key={`private${i}`}
          exact={true}
          path={r.path}
          render={(props) => {
            // 유저가 로그인상태인지 아닌지 모를경우
            if (authStore.userInfo === undefined) {
              return <RoutePending {...props} />
            }
            const Component = r.component
            return authStore.userInfo != null ? <Component {...props} /> : <Redirect to={fallbackUrl} />
          }}
        />
      )
    })
  }

  return (
    <IonApp style={{ maxWidth: '640px', margin: '0 auto' }}>
      <IonTabContainer>
        <IonReactRouter>
          <IonTabs>
            {/* ios에서 swipe-back시 중복 page transition을 막기 위해 transition disable */}
            <IonRouterOutlet animated={isPlatform('android')}>
              {getPublicRoutes([
                { path: '/home', component: Home },
                { path: '/sellHistory', component: SellHistoryDetail },
                { path: '/isSignIn', component: IsSignIn },
                { path: '/signUp', component: SignUp },
                { path: '/signIn', component: SignIn },
                { path: '/signIn/:provider', component: SignInVerify },
                { path: '/market', component: Market },
                { path: '/market/:productId', component: Detail },
                { path: '/market/:productId/trade', component: Trade },
                { path: '/market/:productId/community', component: ProductCommunityMarket },
                { path: '/artfair/:eventId/event', component: DetailEvent },
                { path: '/artfair/:articleId', component: DetailArtMuseum },
                { path: '/artfair', component: Artfair },
                { path: '/notification/', component: Notice },
                { path: '/notification/:notificationId/notice', component: DetailNotice },
                { path: '/sample', component: Sample },
              ])}
              {getPrivateRoutes([
                { path: '/notification/:notificationId/notification', component: DetailNotification },
                { path: '/notification/:notificationId/sell', component: DetailSell },
                { path: '/myPage', component: My },
                { path: '/myPage/asset', component: Asset },
                { path: '/myPage/asset/:productId', component: ProductDetail },
                { path: '/myPage/asset/:voteId/disposal', component: ProductDisposal },
                { path: '/myPage/asset/:productId/community', component: ProductCommunity },
                { path: '/myPage/asset/:productId/description/', component: ProductDescription },
                { path: '/myPage/account', component: Account },
                { path: '/myPage/account/deposit', component: AccountDeposit },
                { path: '/myPage/account/withdrawal', component: AccountWithdrawal },
                { path: '/myPage/qna', component: Qna },
                { path: '/myPage/qna/detail/:qnaId', component: QnaDetail },
                { path: '/myPage/qna/register', component: QnaRegister },
                { path: '/myPage/faq', component: Faq },
                { path: '/myPage/setting', component: Setting },
                { path: '/myPage/setting/nickname', component: SettingNickname },
                { path: '/myPage/setting/wallet', component: SettingWallet },
                { path: '/myPage/setting/account', component: SettingAccount },
                { path: '/myPage/setting/address', component: SettingAddress },
                { path: '/myPage/setting/profile', component: SettingProfile },
                { path: '/myPage/setting/cashReceipt', component: SettingCashReceipt },
                { path: '/myPage/notice', component: NoticeMy },
                { path: '/myPage/notice/:notificationId', component: NoticeDetailMy },
              ])}
              <Route render={() => <RedirectComponent />} />
            </IonRouterOutlet>
            <IonTabBar style={{ display: commonStore.isShowTab ? 'flex' : 'none' }} slot="bottom">
              <IonTabButton tab="home" href="/home">
                <IonLabel>홈</IonLabel>
                <IonIcon icon={homeOutline}></IonIcon>
              </IonTabButton>
              <IonTabButton tab="market" href="/market">
                <IonLabel>마켓</IonLabel>
                <IonIcon icon={repeatOutline}></IonIcon>
              </IonTabButton>
              <IonTabButton tab="artfair" href="/artfair">
                <IonLabel>아트페어</IonLabel>
                <IonIcon icon={gridOutline}></IonIcon>
              </IonTabButton>
              <IonTabButton tab="myPage" href="/myPage">
                <IonLabel>마이</IonLabel>
                <IonIcon icon={personOutline}></IonIcon>
              </IonTabButton>
            </IonTabBar>
          </IonTabs>
        </IonReactRouter>
      </IonTabContainer>
    </IonApp>
  )
})

export default App

const IonTabContainer = styled.div`
  ion-tab-bar {
    height: 60px;
  }
  ion-tab-button {
    --color-selected: var(--ion-color-primary_dark);
    --color: var(--ion-color-gray400);

    ion-label {
      font-size: 11px;
    }
    ion-icon {
      font-size: 24px;
    }
  }
`
